import JSONRPC from './JSONRPC';
import type {
  JsonRpcClient,
  QtTokenObject,
  RpcPromise,
  Token,
  TokenPromise,
} from './types/AccessToken';

export type {
  JsonRpcClient,
  QtTokenObject,
  RpcPromise,
  Token,
  TokenPromise,
} from './types/AccessToken';

function createNullJsonRpc(): JsonRpcClient {
  return { request: <T = unknown>() => Promise.resolve(null as T) };
}

/** Deprecated: TokenApiV1 relies on the App to keep track of the URL which the token is for. */
export class TokenApiV1 {
  private jsonrpc: JsonRpcClient;

  // dummy dispatcher that resolves all calls to null
  constructor() {
    // dummy dispatcher that resolves all calls to null
    this.jsonrpc = createNullJsonRpc();
  }

  connect(qtobject: QtTokenObject): void {
    // connecting all machine actions here
    try {
      this.jsonrpc = new JSONRPC((message) =>
        qtobject.dispatch(JSON.stringify(message))
      );
      qtobject.response.connect((str) =>
        this.jsonrpc.handleResponse?.(JSON.parse(str))
      );
    } catch (e) {
      console.log(e);
      this.jsonrpc = createNullJsonRpc();
    }
  }

  getToken(_url?: string): TokenPromise {
    return this.jsonrpc.request<Token>('get_token');
  }

  setToken(token: Token, _url?: string): RpcPromise {
    return this.jsonrpc.request('set_token', [token]);
  }

  getRefreshToken(_url?: string): TokenPromise {
    return this.jsonrpc.request<Token>('get_refresh_token');
  }

  setRefreshToken(token: Token, _url?: string): RpcPromise {
    return this.jsonrpc.request('set_refresh_token', [token]);
  }
}

/** TokenApiV2 passes the URL as a parameter to the methods */
export class TokenApiV2 {
  private jsonrpc: JsonRpcClient;

  constructor() {
    // dummy dispatcher that resolves all calls to null
    this.jsonrpc = createNullJsonRpc();
  }

  connect(qtobject: QtTokenObject): void {
    try {
      this.jsonrpc = new JSONRPC((message) =>
        qtobject.dispatch(JSON.stringify(message))
      );
      qtobject.response.connect((str) =>
        this.jsonrpc.handleResponse?.(JSON.parse(str))
      );
    } catch (e) {
      console.log(e);
      this.jsonrpc = createNullJsonRpc();
    }
  }

  getToken(url?: string): TokenPromise {
    return this.jsonrpc.request<Token>('get_token', ['access_token', url]);
  }

  setToken(token: Token, url?: string): RpcPromise {
    return this.jsonrpc.request('set_token', [token, 'access_token', url]);
  }
  getRefreshToken(url?: string): TokenPromise {
    return this.jsonrpc.request<Token>('get_token', ['refresh_token', url]);
  }
  setRefreshToken(token: Token, url?: string): RpcPromise {
    return this.jsonrpc.request('set_token', [token, 'refresh_token', url]);
  }
}

/** Wrapper to redirect calls to the most current version available */
export class TokenApi {
  private api1: TokenApiV1;
  private api2: TokenApiV2;
  private api: TokenApiV1 | TokenApiV2;
  version: number;

  constructor() {
    this.api1 = new TokenApiV1();
    this.api2 = new TokenApiV2();
    this.api = this.api1; // Default to whichever
    this.version = 1; // Default version
  }

  connect(qtobject: QtTokenObject, version = 1): void {
    this.version = version;
    this.api = version === 2 ? this.api2 : this.api1;
    this.api.connect(qtobject);
  }

  getToken(url?: string): TokenPromise {
    return this.api.getToken(url);
  }
  setToken(token: Token, url?: string): RpcPromise {
    return this.api.setToken(token, url);
  }
  getRefreshToken(url?: string): TokenPromise {
    return this.api.getRefreshToken(url);
  }
  setRefreshToken(token: Token, url?: string): RpcPromise {
    return this.api.setRefreshToken(token, url);
  }
}

const api = new TokenApi();

export { api as default };
